import { Link } from "react-router-dom";
import { ArrowRight, Anchor, Weight, Timer, Container } from "lucide-react";
import SEO from "../components/SEO";
import CTASection from "../components/CTASection";
import Marquee from "../components/Marquee";
import { Reveal, MaskedLine, Eyebrow } from "../components/Reveal";
import { IMAGES, VOLUME_STATS } from "../data/site";

const CHAPTERS = [
    {
        num: "01",
        title: "Family-Owned Since 2002",
        copy: "Pacific Expressway is a family-owned Southern California logistics company serving customers since 2002. That means long-term relationships, hands-on service, and decisions made by people whose name is on the trucks — not a call center.",
    },
    {
        num: "02",
        title: "A Dedicated Fleet",
        copy: "We operate our own trucks, chassis and equipment. When capacity tightens across the port complex, our customers aren't waiting on a broker's phone calls — they're backed by assets we control.",
    },
    {
        num: "03",
        title: "Built for High Volume",
        copy: "Dual yards, a 90K top pick and 900+ containers a week. Our infrastructure was built for demanding, high-frequency freight operations — and for the problems that come with them.",
    },
];

const COMPLICATIONS = [
    {
        icon: Anchor,
        title: "Port Congestion",
        copy: "When terminals back up, yard space and dual-yard flexibility keep containers accessible instead of trapped.",
    },
    {
        icon: Weight,
        title: "Overweight & Difficult Loads",
        copy: "Overweight corridor capability, a 90K top pick and in-house rework turn rejected loads into delivered freight.",
    },
    {
        icon: Container,
        title: "Equipment Availability",
        copy: "500+ chassis mean your box isn't sitting on the ground waiting for equipment that may never show.",
    },
    {
        icon: Timer,
        title: "Coordination Under Pressure",
        copy: "Two decades of LA/LB port experience coordinating terminals, warehouses and delivery windows that don't slip.",
    },
];

export default function WhyPacific() {
    return (
        <>
            <SEO
                title="Why Pacific Expressway | Dedicated Fleet, Family-Owned Since 2002"
                description="Family-owned Southern California logistics since 2002. A dedicated fleet of 100+ trucks and 500+ chassis, dual yards, LA/LB port expertise and problem-freight experience — not just another broker."
                path="/why-pacific"
            />
            <section
                className="relative flex min-h-[62svh] items-end overflow-hidden bg-ink"
                data-testid="why-hero"
            >
                <div className="absolute inset-0" aria-hidden="true">
                    <img src={IMAGES.containersAerial} alt="" className="h-full w-full object-cover opacity-50" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/40" aria-hidden="true" />
                <div className="container-px relative w-full pb-14 pt-40 lg:pb-20">
                    <Eyebrow>Why Pacific</Eyebrow>
                    <h1 className="mt-6 max-w-4xl font-heading text-3xl sm:text-4xl lg:text-[56px] font-bold tracking-tight leading-[1.15] sm:leading-[1.1] lg:leading-[1.06] text-white">
                        <MaskedLine delay={0.2}>Built on Reliability.</MaskedLine>
                        <MaskedLine delay={0.32}>
                            Backed by Capacity<span className="text-gold">.</span>
                        </MaskedLine>
                    </h1>
                    <Reveal delay={0.5} className="mt-6 max-w-2xl">
                        <p className="text-base sm:text-lg leading-relaxed text-gray-300">
                            More than two decades of family ownership, a dedicated fleet,
                            and the infrastructure to say yes when others can&rsquo;t.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="bg-white py-16 sm:py-20 lg:py-28" data-testid="about-section">
                <div className="container-px grid gap-12 lg:grid-cols-5 lg:gap-16">
                    <Reveal className="lg:col-span-2">
                        <Eyebrow>About Pacific Expressway</Eyebrow>
                        <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-ink">
                            Hands-On Service. Industrial Scale.
                        </h2>
                        <p className="mt-6 text-base sm:text-lg leading-relaxed text-gray-600">
                            Pacific Expressway combines the accountability of a family-owned
                            company with the fleet, equipment and infrastructure required to
                            support the most demanding freight operations in Southern
                            California.
                        </p>
                        <div className="group relative mt-10 overflow-hidden">
                            <img
                                src={IMAGES.aboutTrucks}
                                alt="Class 8 trucks running a Southern California highway at golden hour"
                                loading="lazy"
                                className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                        </div>
                    </Reveal>
                    <div className="lg:col-span-3">
                        <ol className="divide-y divide-gray-200 border-y border-gray-200">
                            {CHAPTERS.map((chapter, i) => (
                                <Reveal key={chapter.num} delay={i * 0.08}>
                                    <li className="grid gap-4 py-8 sm:grid-cols-[auto_1fr] sm:gap-8 sm:py-10">
                                        <span
                                            className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-gold/90"
                                            aria-hidden="true"
                                        >
                                            {chapter.num}
                                        </span>
                                        <div>
                                            <h3 className="font-heading text-xl sm:text-2xl font-semibold text-ink">
                                                {chapter.title}
                                            </h3>
                                            <p className="mt-3 text-[15px] sm:text-base leading-relaxed text-gray-600">
                                                {chapter.copy}
                                            </p>
                                        </div>
                                    </li>
                                </Reveal>
                            ))}
                        </ol>
                    </div>
                </div>
            </section>

            <Marquee />

            <section className="bg-ink py-16 sm:py-20 lg:py-28 grain relative" data-testid="capacity-proof">
                <div className="container-px">
                    <Reveal className="max-w-3xl">
                        <Eyebrow>Verified Capacity</Eyebrow>
                        <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
                            The Numbers Behind the Promise.
                        </h2>
                    </Reveal>
                    <div className="mt-12 grid grid-cols-2 gap-px bg-white/10 lg:mt-16 lg:grid-cols-3">
                        {VOLUME_STATS.map((stat, i) => (
                            <Reveal key={stat.label} delay={(i % 3) * 0.08} className="bg-ink">
                                <div className="flex h-full flex-col justify-between p-6 sm:p-10">
                                    <p className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-gold">
                                        {stat.value}
                                    </p>
                                    <p className="mt-4 text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-gray-400">
                                        {stat.label}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-mist py-16 sm:py-20 lg:py-28" data-testid="not-a-broker">
                <div className="container-px grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
                    <Reveal>
                        <Eyebrow>The Pacific Difference</Eyebrow>
                        <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-ink">
                            Not Just Another Broker.
                        </h2>
                        <p className="mt-6 text-base sm:text-lg leading-relaxed text-gray-600">
                            Many logistics providers sell capacity they don&rsquo;t own. When
                            you work with Pacific, you work with the company that owns the
                            trucks, the chassis, the yards and the heavy equipment handling
                            your freight.
                        </p>
                        <p className="mt-4 text-base sm:text-lg leading-relaxed text-gray-600">
                            That means real accountability: one relationship, one dispatch
                            team, and assets we control — from the port to the warehouse to
                            the final destination.
                        </p>
                        <Link
                            to="/contact"
                            data-testid="broker-quote-button"
                            className="group mt-8 inline-flex items-center gap-2 font-heading text-sm font-bold uppercase tracking-[0.16em] text-ink transition-colors hover:text-gold"
                        >
                            Work With Us
                            <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                        </Link>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div className="group relative overflow-hidden">
                            <img
                                src={IMAGES.trucksHighway}
                                alt="Pacific Expressway dedicated fleet of Class 8 trucks"
                                loading="lazy"
                                className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                            <div className="absolute bottom-0 left-0 bg-ink px-5 py-3">
                                <p className="font-heading text-xs font-bold uppercase tracking-[0.2em] text-gold">
                                    100+ Trucks — Company-Owned
                                </p>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="bg-charcoal py-16 sm:py-20 lg:py-28 grain relative" data-testid="experience-matters">
                <div className="container-px">
                    <Reveal className="max-w-3xl">
                        <Eyebrow>Problem Freight</Eyebrow>
                        <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
                            When Freight Gets Complicated, Experience Matters.
                        </h2>
                    </Reveal>
                    <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:mt-16 lg:gap-8">
                        {COMPLICATIONS.map((item, i) => (
                            <Reveal key={item.title} delay={(i % 2) * 0.08}>
                                <div className="h-full border border-white/10 bg-ink/40 p-7 sm:p-9 transition-colors duration-300 hover:border-gold/40">
                                    <item.icon size={26} className="text-gold" aria-hidden="true" strokeWidth={1.75} />
                                    <h3 className="mt-5 font-heading text-lg sm:text-xl font-semibold text-white">
                                        {item.title}
                                    </h3>
                                    <p className="mt-3 text-[15px] leading-relaxed text-gray-400">
                                        {item.copy}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <CTASection />
        </>
    );
}
